import { useState, useEffect, useCallback } from 'react';
import type { CrossingMessagesConfig, CrossingTemplateConfig } from '../template/MasterDocumentTemplate.types';
import type {
  CrossingClassOption,
  CrossingSourceDocument,
  LineFulfillmentStatus,
  PendingLine,
  TraceabilityTreeNode,
} from '../MasterDocument.types';
import { isLineLevelCrossingError, resolveCrossingError } from './crossingFetch';
import { useMasterDocumentTranslation } from '../i18n';
import type { CrossingFetch } from './crossingFetch';
import type { TranslateFn } from '../i18nFactory';

/**
 * Mensajes de validación de "Cantidad a cruzar" (FE-02), sobreescribibles vía template.
 *
 * Recibe `t` en vez de llamar al hook porque es una función pura; el hook se lo
 * pasa. El override del template sigue teniendo prioridad sobre el catálogo.
 */
function validateQuantity(
  quantity: number,
  pending: number,
  t: TranslateFn,
  uom?: string,
  messages?: CrossingMessagesConfig,
): string | undefined {
  if (Number.isNaN(quantity)) {
    return messages?.quantityRequired ?? t('master_document.crossing.quantityRequired');
  }
  if (quantity <= 0) {
    return messages?.quantityPositive ?? t('master_document.crossing.quantityPositive');
  }
  if (quantity > pending) {
    // El override del template conserva su contrato original: `{uom}` recibe la
    // unidad sin espacio previo, porque la plantilla del host ya lo incluye.
    if (messages?.quantityExceedsPending) {
      return messages.quantityExceedsPending
        .replaceAll('{pending}', String(pending))
        .replaceAll('{uom}', uom ?? '')
        .trim();
    }
    // La clave del catálogo sí espera el espacio dentro del propio valor, para
    // que sin unidad no quede un hueco antes del paréntesis de cierre.
    return t('master_document.crossing.quantityExceedsPending', {
      pending: String(pending),
      uom: uom ? ` ${uom}` : '',
    });
  }
  return undefined;
}

interface UseCrossingOptions {
  config: CrossingTemplateConfig | undefined;
  documentId: string | undefined;
  docState: string;
  hasLines: boolean;
  /**
   * Salida HTTP del panel de cruce. Enruta por `adapter.genericFetch` (auth,
   * baseUrl, remapeo de rutas) — ver {@link createCrossingFetch}.
   */
  crossingFetch: CrossingFetch;
  onDocumentGenerated?: (docId: string) => void;
}

export interface CrossingState {
  selectedSourceDocuments: CrossingSourceDocument[];
  sourceSelectorLocked: boolean;

  selectedLineIds: string[];
  lineStatuses: Record<string, LineFulfillmentStatus>;
  lineSelectionErrors: Record<string, string>;
  pendingLines: PendingLine[];
  quantitiesToCross: Record<string, number>;
  canGenerate: boolean;
  pendingLinesFetchedAt: string | null;
  pendingLinesLoading: boolean;

  crossingClassOptions: CrossingClassOption[];
  crossingClassMenuOpen: boolean;

  lineCloseModal: {
    open: boolean;
    lineId: string | null;
    lineDescription: string;
    mode: 'close' | 'reopen';
    isLoading: boolean;
    error: string | undefined;
  };

  generateModal: {
    open: boolean;
    classId: string;
    classLabel: string;
    isLoading: boolean;
    error: string | undefined;
  };

  generateError: string | undefined;
  pageErrors: string[];

  activeTab: 'lines' | 'traceability';
  traceabilityTree: TraceabilityTreeNode | null;
  traceabilityLoading: boolean;
  traceabilityError: string | undefined;
}

export interface CrossingActions {
  addSourceDocument: (doc: CrossingSourceDocument) => void;
  removeSourceDocument: (docId: string) => void;
  searchSourceDocuments: (query: string) => Promise<CrossingSourceDocument[]>;
  toggleLineSelection: (lineId: string, selected: boolean) => void;
  trySelectTerminalLine: (lineId: string) => void;
  setQuantityToCross: (lineId: string, quantity: number) => void;
  refreshPendingLines: () => void;
  openCrossClassMenu: () => void;
  closeCrossClassMenu: () => void;
  selectClass: (classId: string) => void;
  generateChild: (classId: string, headerValues?: Record<string, unknown>) => Promise<string | null>;
  openLineCloseModal: (lineId: string, description: string, mode: 'close' | 'reopen') => void;
  closeLineCloseModal: () => void;
  confirmLineClose: (reason?: string) => Promise<void>;
  openGenerateModal: (classId: string, classLabel: string) => void;
  cancelGenerateModal: () => void;
  confirmGenerate: (headerValues: Record<string, unknown>) => Promise<void>;
  clearGenerateError: () => void;
  addPageError: (message: string) => void;
  dismissPageError: (index: number) => void;
  setActiveTab: (tab: 'lines' | 'traceability') => void;
  refreshTraceability: () => void;
}

// ─── mapNode helper ───────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapNode(raw: any): TraceabilityTreeNode {
  return {
    id: raw.id,
    number: raw.number,
    classLabel: raw.class_label,
    status: raw.status,
    quantityOrdered: raw.quantity_ordered,
    quantityFulfilled: raw.quantity_fulfilled,
    isCurrent: raw.is_current,
    parents: (raw.parents ?? []).map(mapNode),
    children: (raw.children ?? []).map(mapNode),
  };
}

// ─── mapPendingLine helper ────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapPendingLine(raw: any, fallbackSourceId: string): PendingLine {
  return {
    lineId: String(raw.line_id ?? raw.id ?? raw.lineId),
    sourceDocumentId: String(raw.source_document_id ?? raw.document_id ?? fallbackSourceId),
    documentNumber: raw.document_number ?? raw.documentNumber,
    itemCode: raw.item_code ?? raw.itemCode,
    itemDescription: raw.item_description ?? raw.itemDescription,
    quantityOrdered: raw.quantity_ordered ?? raw.quantityOrdered,
    quantityFulfilled: raw.quantity_fulfilled ?? raw.quantityFulfilled,
    quantityPending: Number(raw.quantity_pending ?? raw.quantityPending ?? 0),
    unitOfMeasure: raw.unit_of_measure ?? raw.unitOfMeasure,
    unitPrice: raw.unit_price ?? raw.unitPrice ?? null,
    lineStatus: (raw.line_status ?? raw.lineStatus ?? 'Pending') as LineFulfillmentStatus,
  };
}

// ─── Internal hook (always called unconditionally) ────────────

function useCrossingInternal({
  config,
  documentId,
  hasLines,
  crossingFetch,
  onDocumentGenerated,
}: UseCrossingOptions): { state: CrossingState; actions: CrossingActions } {
  // Traduce los errores que genera el kit; los que trae el backend pasan intactos.
  const { t } = useMasterDocumentTranslation();
  const [selectedSourceDocuments, setSelectedSourceDocuments] = useState<CrossingSourceDocument[]>([]);
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);
  const [lineStatuses, setLineStatuses] = useState<Record<string, LineFulfillmentStatus>>({});
  const [lineSelectionErrors, setLineSelectionErrors] = useState<Record<string, string>>({});
  const [pendingLines, setPendingLines] = useState<PendingLine[]>([]);
  const [quantitiesToCross, setQuantitiesToCross] = useState<Record<string, number>>({});
  const [pendingLinesFetchedAt, setPendingLinesFetchedAt] = useState<string | null>(null);
  const [pendingLinesLoading, setPendingLinesLoading] = useState(false);
  const [crossingClassOptions, setCrossingClassOptions] = useState<CrossingClassOption[]>([]);
  const [crossingClassMenuOpen, setCrossingClassMenuOpen] = useState(false);
  const [lineCloseModal, setLineCloseModal] = useState<CrossingState['lineCloseModal']>({
    open: false,
    lineId: null,
    lineDescription: '',
    mode: 'close',
    isLoading: false,
    error: undefined,
  });
  const [generateModal, setGenerateModal] = useState<CrossingState['generateModal']>({
    open: false,
    classId: '',
    classLabel: '',
    isLoading: false,
    error: undefined,
  });
  const [generateError, setGenerateError] = useState<string | undefined>(undefined);
  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'lines' | 'traceability'>('lines');
  const [traceabilityTree, setTraceabilityTree] = useState<TraceabilityTreeNode | null>(null);
  const [traceabilityLoading, setTraceabilityLoading] = useState(false);
  const [traceabilityError, setTraceabilityError] = useState<string | undefined>(undefined);

  // ── Fetch crossing rules on mount ─────────────────────────
  useEffect(() => {
    if (!config || !config.enabled) return;
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    crossingFetch<any>('GET', '/api/v1/crossing/rules')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((payload: any) => {
        if (cancelled) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rules: any[] = Array.isArray(payload) ? payload : (payload?.data ?? []);
        setCrossingClassOptions(
          rules.map((rule) => ({
            id: rule.child_class_id,
            label: rule.child_class_label,
            description: rule.description,
          })),
        );
      })
      // Carga no crítica: si falla, el menú de clases queda vacío (sin bloquear).
      .catch(() => { /* opciones de cruce no disponibles */ });
    return () => { cancelled = true; };
  }, [config, crossingFetch]);

  // ── Fetch pending lines (agrega TODOS los documentos origen — FE-01) ──
  const fetchPendingLines = useCallback(() => {
    if (!config || !config.enabled) return;
    if (selectedSourceDocuments.length === 0) {
      setPendingLines([]);
      setLineStatuses({});
      return;
    }
    const childClassId = crossingClassOptions[0]?.id ?? '';
    setPendingLinesLoading(true);

    Promise.all(
      selectedSourceDocuments.map((doc) =>
        crossingFetch<unknown>(
          'GET',
          `/api/v1/crossing/pending-lines?source_document_id=${encodeURIComponent(doc.id)}&child_class_id=${encodeURIComponent(childClassId)}`,
        )
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .then((payload: any) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const rows: any[] = Array.isArray(payload) ? payload : (payload?.lines ?? []);
            return rows.map((row) => mapPendingLine(row, doc.id));
          }),
      ),
    )
      .then((perDoc) => {
        const merged = perDoc.flat();
        setPendingLines(merged);
        const map: Record<string, LineFulfillmentStatus> = {};
        for (const line of merged) map[line.lineId] = line.lineStatus;
        setLineStatuses(map);
        setPendingLinesFetchedAt(new Date().toISOString());
      })
      .catch((err) => {
        const { message } = resolveCrossingError(err, t);
        setPageErrors((prev) => (prev.includes(message) ? prev : [...prev, message]));
      })
      .finally(() => setPendingLinesLoading(false));
  }, [config, selectedSourceDocuments, crossingClassOptions, crossingFetch, t]);

  useEffect(() => {
    if (!config || !config.enabled) return;
    if (selectedSourceDocuments.length === 0) return;
    fetchPendingLines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSourceDocuments, config]);

  // ── Fetch traceability tree ───────────────────────────────
  const fetchTraceabilityTree = useCallback(() => {
    if (!config || !config.enabled) return;
    if (!documentId) return;
    setTraceabilityLoading(true);
    setTraceabilityError(undefined);
    crossingFetch<unknown>(
      'GET',
      `/api/v1/crossing/document-tree?document_id=${encodeURIComponent(documentId)}`,
    )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((payload: any) => {
        const raw = payload?.root ?? payload;
        setTraceabilityTree(mapNode(raw));
      })
      .catch((err) => {
        setTraceabilityError(resolveCrossingError(err, t).message);
      })
      .finally(() => setTraceabilityLoading(false));
  }, [config, documentId, crossingFetch, t]);

  useEffect(() => {
    if (!config || !config.enabled) return;
    if (!documentId) return;
    fetchTraceabilityTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, config]);

  // ── Actions ───────────────────────────────────────────────

  const addSourceDocument = useCallback((doc: CrossingSourceDocument) => {
    setSelectedSourceDocuments((prev) => {
      if (prev.some((d) => d.id === doc.id)) return prev;
      return [...prev, doc];
    });
  }, []);

  const removeSourceDocument = useCallback((docId: string) => {
    setSelectedSourceDocuments((prev) => prev.filter((d) => d.id !== docId));
  }, []);

  const searchSourceDocuments = useCallback(
    async (query: string): Promise<CrossingSourceDocument[]> => {
      if (!config || !query.trim()) return [];
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload: any = await crossingFetch<any>(
          'GET',
          `/api/v1/crossing/search-documents?q=${encodeURIComponent(query)}`,
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const docs: any[] = Array.isArray(payload) ? payload : (payload?.data ?? []);
        return docs.map((d) => ({
          id: String(d.id),
          number: String(d.number ?? d.document_number ?? ''),
          classLabel: String(d.class_label ?? d.classLabel ?? ''),
        }));
      } catch {
        // Búsqueda type-ahead: ante error devolvemos lista vacía (sin bloquear).
        return [];
      }
    },
    [config, crossingFetch],
  );

  const toggleLineSelection = useCallback(
    (lineId: string, selected: boolean) => {
      setSelectedLineIds((prev) =>
        selected ? [...prev.filter((id) => id !== lineId), lineId] : prev.filter((id) => id !== lineId),
      );
      if (selected) {
        // Al seleccionar, predefine la cantidad a cruzar = pendiente de la línea.
        const pending = pendingLines.find((l) => l.lineId === lineId)?.quantityPending;
        if (pending != null) {
          setQuantitiesToCross((prev) => ({ ...prev, [lineId]: pending }));
        }
      } else {
        // Al deseleccionar, limpia cantidad y error de esa línea.
        setQuantitiesToCross((prev) => {
          const next = { ...prev };
          delete next[lineId];
          return next;
        });
        setLineSelectionErrors((prev) => {
          const next = { ...prev };
          delete next[lineId];
          return next;
        });
      }
    },
    [pendingLines],
  );

  const trySelectTerminalLine = useCallback(
    (lineId: string) => {
      setLineSelectionErrors((prev) => ({
        ...prev,
        [lineId]:
          config?.messages?.terminalLineNotSelectable
          ?? t('master_document.crossing.terminalLineMessage'),
      }));
    },
    [config, t],
  );

  const setQuantityToCross = useCallback(
    (lineId: string, quantity: number) => {
      setQuantitiesToCross((prev) => ({ ...prev, [lineId]: quantity }));
      const line = pendingLines.find((l) => l.lineId === lineId);
      const message = validateQuantity(
        quantity,
        line?.quantityPending ?? 0,
        t,
        line?.unitOfMeasure,
        config?.messages,
      );
      setLineSelectionErrors((prev) => {
        const next = { ...prev };
        if (message) next[lineId] = message;
        else delete next[lineId];
        return next;
      });
    },
    [pendingLines, config, t],
  );

  const openCrossClassMenu = useCallback(() => setCrossingClassMenuOpen(true), []);
  const closeCrossClassMenu = useCallback(() => setCrossingClassMenuOpen(false), []);
  // Selecciona una clase hija del menú: cierra el menú y abre el modal de header
  // obligatorio con la clase elegida (FE-06). El flujo de generación continúa en
  // confirmGenerate.
  const selectClass = useCallback((classId: string) => {
    setCrossingClassMenuOpen(false);
    setCrossingClassOptions((options) => {
      const classLabel = options.find((o) => o.id === classId)?.label ?? classId;
      setGenerateModal({ open: true, classId, classLabel, isLoading: false, error: undefined });
      return options;
    });
    setGenerateError(undefined);
  }, []);

  // Lanza en error; usado internamente por confirmGenerate para exponer el
  // mensaje del backend. La acción pública `generateChild` lo envuelve y
  // devuelve null ante fallo para preservar su contrato histórico.
  const generateChildInternal = useCallback(
    async (classId: string, headerValues?: Record<string, unknown>): Promise<string> => {
      const sourceDocId = selectedSourceDocuments[0]?.id;
      if (!sourceDocId || selectedLineIds.length === 0) {
        throw new Error(t('master_document.crossing.selectLineToGenerate'));
      }
      // selected_lines lleva la cantidad a cruzar por línea (FE-02). Espeja
      // GenerateChildDocumentDto.SelectedLines del backend.
      const selectedLines = selectedLineIds.map((lineId) => {
        const line = pendingLines.find((l) => l.lineId === lineId);
        return {
          source_line_id: lineId,
          source_document_id: line?.sourceDocumentId ?? sourceDocId,
          quantity_to_cross: quantitiesToCross[lineId] ?? line?.quantityPending ?? 0,
        };
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = await crossingFetch<any>('POST', '/api/v1/crossing/generate', {
        source_document_id: sourceDocId,
        child_class_id: classId,
        line_ids: selectedLineIds, // compat
        selected_lines: selectedLines,
        header_values: headerValues ?? {},
      });
      const docId = payload?.document_id ?? payload?.documentId ?? null;
      if (!docId) {
        throw new Error(t('master_document.crossing.noGeneratedDocument'));
      }
      return String(docId);
    },
    [selectedSourceDocuments, selectedLineIds, pendingLines, quantitiesToCross, crossingFetch, t],
  );

  const generateChild = useCallback(
    async (classId: string, headerValues?: Record<string, unknown>): Promise<string | null> => {
      try {
        return await generateChildInternal(classId, headerValues);
      } catch {
        return null;
      }
    },
    [generateChildInternal],
  );

  const openLineCloseModal = useCallback(
    (lineId: string, description: string, mode: 'close' | 'reopen') => {
      setLineCloseModal({ open: true, lineId, lineDescription: description, mode, isLoading: false, error: undefined });
    },
    [],
  );

  const closeLineCloseModal = useCallback(() => {
    setLineCloseModal((prev) => ({ ...prev, open: false, lineId: null, error: undefined }));
  }, []);

  const openGenerateModal = useCallback((classId: string, classLabel: string) => {
    setGenerateModal({ open: true, classId, classLabel, isLoading: false, error: undefined });
    setGenerateError(undefined);
  }, []);

  const cancelGenerateModal = useCallback(() => {
    setGenerateModal((prev) => ({ ...prev, open: false, error: undefined }));
  }, []);

  const confirmGenerate = useCallback(
    async (headerValues: Record<string, unknown>) => {
      setGenerateModal((prev) => ({ ...prev, isLoading: true, error: undefined }));
      const classId = generateModal.classId;
      try {
        const docId = await generateChildInternal(classId, headerValues);
        setGenerateModal({ open: false, classId: '', classLabel: '', isLoading: false, error: undefined });
        onDocumentGenerated?.(docId);
      } catch (err) {
        setGenerateModal((prev) => ({
          ...prev,
          isLoading: false,
          error: resolveCrossingError(err, t).message,
        }));
      }
    },
    [generateModal.classId, generateChildInternal, onDocumentGenerated, t],
  );

  const clearGenerateError = useCallback(() => {
    setGenerateError(undefined);
  }, []);

  const addPageError = useCallback((message: string) => {
    setPageErrors((prev) => [...prev, message]);
  }, []);

  const dismissPageError = useCallback((index: number) => {
    setPageErrors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const confirmLineClose = useCallback(
    async (reason?: string) => {
      setLineCloseModal((prev) => ({ ...prev, isLoading: true, error: undefined }));
      const lineId = lineCloseModal.lineId;
      const mode = lineCloseModal.mode;
      if (!lineId) return;
      try {
        if (mode === 'close') {
          await crossingFetch('POST', `/api/v1/crossing/lines/${encodeURIComponent(lineId)}/close`, {
            closeReason: reason,
          });
        } else {
          await crossingFetch('POST', `/api/v1/crossing/lines/${encodeURIComponent(lineId)}/reopen`);
        }
        setLineStatuses((prev) => ({
          ...prev,
          [lineId]: mode === 'close' ? 'Closed' : 'Pending',
        }));
        setLineCloseModal({ open: false, lineId: null, lineDescription: '', mode: 'close', isLoading: false, error: undefined });
        fetchPendingLines();
      } catch (err) {
        const { errorCode, message } = resolveCrossingError(err, t);
        setLineCloseModal((prev) => ({ ...prev, isLoading: false, error: message }));
        // FE-08: los errores de línea (BR-01/11/16) también se reflejan inline
        // junto a la fila afectada.
        if (isLineLevelCrossingError(errorCode)) {
          setLineSelectionErrors((prev) => ({ ...prev, [lineId]: message }));
        }
      }
    },
    [lineCloseModal.lineId, lineCloseModal.mode, crossingFetch, fetchPendingLines, t],
  );

  const handleSetActiveTab = useCallback((tab: 'lines' | 'traceability') => {
    setActiveTab(tab);
  }, []);

  // Habilita generar/cruzar solo si hay líneas seleccionadas, ninguna con error
  // de validación, y todas con cantidad > 0 (FE-02).
  const canGenerate =
    selectedLineIds.length > 0 &&
    selectedLineIds.every((id) => !lineSelectionErrors[id]) &&
    selectedLineIds.every((id) => {
      const q = quantitiesToCross[id];
      return q != null && !Number.isNaN(q) && q > 0;
    });

  const state: CrossingState = {
    selectedSourceDocuments,
    sourceSelectorLocked: hasLines,
    selectedLineIds,
    lineStatuses,
    lineSelectionErrors,
    pendingLines,
    quantitiesToCross,
    canGenerate,
    pendingLinesFetchedAt,
    pendingLinesLoading,
    crossingClassOptions,
    crossingClassMenuOpen,
    lineCloseModal,
    generateModal,
    generateError,
    pageErrors,
    activeTab,
    traceabilityTree,
    traceabilityLoading,
    traceabilityError,
  };

  const actions: CrossingActions = {
    addSourceDocument,
    removeSourceDocument,
    searchSourceDocuments,
    toggleLineSelection,
    trySelectTerminalLine,
    setQuantityToCross,
    refreshPendingLines: fetchPendingLines,
    openCrossClassMenu,
    closeCrossClassMenu,
    selectClass,
    generateChild,
    openLineCloseModal,
    closeLineCloseModal,
    confirmLineClose,
    openGenerateModal,
    cancelGenerateModal,
    confirmGenerate,
    clearGenerateError,
    addPageError,
    dismissPageError,
    setActiveTab: handleSetActiveTab,
    refreshTraceability: fetchTraceabilityTree,
  };

  return { state, actions };
}

// ─── Public hook ──────────────────────────────────────────────

export function useCrossing(
  options: UseCrossingOptions,
): { state: CrossingState; actions: CrossingActions } | null {
  const result = useCrossingInternal(options);
  if (!options.config || !options.config.enabled) return null;
  return result;
}
